import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonRouterLink } from '@ionic/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import { setMe } from '@actions/sessionActions';
import BaseErrors from '@components/fields/BaseErrors';
import styles from '@components/forms/EmailSecurityForm.module.css';
import Form from '@components/forms/Form';
import FormCondition from '@components/forms/FormCondition';
import Icon from '@components/text/Icon';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import { sessionContext } from '@context/SessionStore';
import useMyClient from '@hooks/useMyClient';
import {
  ClientUpdateDocument,
  ClientUpdateMutation,
  ClientUpdateMutationVariables,
  EmailFrequencyEnum,
  EmailSecurityEnum,
  GetClientWithAccountQuery
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  emailFrequency: EmailFrequencyEnum;
  emailSecurity: EmailSecurityEnum;
};

type Props = {
  client: GetClientWithAccountQuery['client'];
};

type EmailFrequencyControlsProps = {
  form: UseFormReturn<FormData, any, undefined>;
};

const EmailFrequencyControls = ({ form }: EmailFrequencyControlsProps) => {
  const emailFrequencyOptions = [EmailFrequencyEnum.each_message, EmailFrequencyEnum.daily_digest];

  return (
    <Controller
      control={form.control}
      name="emailFrequency"
      render={({ field: { onChange, value } }) => (
        <div className={styles.frequencyOptions}>
          {emailFrequencyOptions.map(option => (
            <div className={styles.option} key={option}>
              <IonRouterLink onClick={() => onChange(option)}>
                <div className={styles.frequencyOptionRow}>
                  <Icon
                    ariaLabelKey="accessibility.ariaLabels.alert"
                    className={`${styles.icon} ${value === option ? styles.selected : ''}`}
                    icon={value === option ? 'circleDot' : 'circle'}
                  />
                  <BodyCopy color="neutral300">
                    <FormattedMessage id={`models.account.emailFrequency.title.${option}`} />
                  </BodyCopy>
                </div>
              </IonRouterLink>
            </div>
          ))}
        </div>
      )}
    />
  );
};

const EmailSecurityForm = ({ client }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { dispatch } = useContext(sessionContext);

  const [clientUpdate] = useMutation<ClientUpdateMutation, ClientUpdateMutationVariables>(ClientUpdateDocument);
  const schema = yup
    .object()
    .shape({
      emailSecurity: yup.string()
    })
    .required();

  const defaultValues = {
    emailFrequency: client.emailFrequency,
    emailSecurity: client.emailSecurity
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const watchEmailSecurity = form.watch('emailSecurity');
  const watchEmailFrequency = form.watch('emailFrequency');

  const handleChange = useCallback(async () => {
    setBaseErrors([]);
    return clientUpdate({
      variables: {
        id: client.id,
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.clientUpdate,
        intl,
        onSuccess: () => {
          if (response.data?.clientUpdate?.client) {
            if (myClient && response.data.clientUpdate.client.id === myClient?.id) {
              dispatch(setMe(response.data.clientUpdate.client));
            }
            setSubmitting(false);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.clientAccountUpdate'
      });
    });
  }, [client.id, clientUpdate, dispatch, form, intl, myClient]);

  useEffect(() => {
    if (!submitting && (watchEmailSecurity !== client.emailSecurity || watchEmailFrequency !== client.emailFrequency)) {
      setSubmitting(true);
      handleChange();
    }
  }, [client.emailFrequency, client.emailSecurity, handleChange, submitting, watchEmailFrequency, watchEmailSecurity]);

  const emailSecurityOptions = [EmailSecurityEnum.full, EmailSecurityEnum.snippet, EmailSecurityEnum.private];

  return (
    <Form data-test="email-security-form" hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <Controller
        control={form.control}
        name="emailSecurity"
        render={({ field: { onChange, value } }) => (
          <div className={styles.securityOptions}>
            {emailSecurityOptions.map(option => (
              <div className={styles.option} key={option}>
                <IonRouterLink onClick={() => onChange(option)}>
                  <div className={styles.securityOptionRow}>
                    <Icon
                      ariaLabelKey="accessibility.ariaLabels.alert"
                      className={`${styles.icon} ${value === option ? styles.selected : ''}`}
                      icon={value === option ? 'circleDot' : 'circle'}
                    />
                    <div className={styles.securityOptionColumn}>
                      <BodyCopyBold color="primary700">
                        <FormattedMessage id={`models.account.emailSecurity.title.${option}`} />
                      </BodyCopyBold>
                      <BodyCopy color="neutral300">
                        <FormattedMessage id={`models.account.emailSecurity.description.${option}`} />
                      </BodyCopy>
                      <FormCondition condition={option === value} fieldName="emailFrequency" form={form}>
                        <EmailFrequencyControls form={form} />
                      </FormCondition>
                    </div>
                  </div>
                </IonRouterLink>
              </div>
            ))}
          </div>
        )}
      />
    </Form>
  );
};

export default EmailSecurityForm;
